import { Columns2, MessageSquare, Minimize2, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChatThread } from "./ChatThread";

interface ExpandedChatProps {
  campaignId: string;
  campaignName: string;
  mode: "expanded" | "split";
  onMinimize: () => void;
  onToggleSplit: () => void;
  onSideEffect: (action: Record<string, unknown> & { type: string }) => void;
}

export function ExpandedChat({
  campaignId,
  campaignName,
  mode,
  onMinimize,
  onToggleSplit,
  onSideEffect,
}: ExpandedChatProps) {
  const [clearing, setClearing] = useState(false);
  const [clearKey, setClearKey] = useState(0);

  const handleClear = useCallback(async () => {
    setClearing(true);
    try {
      const res = await fetch(`/api/campaigns/${campaignId}/chat`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to clear chat");
      setClearKey((k) => k + 1);
      toast.success("Chat cleared");
    } catch {
      toast.error("Failed to clear chat");
    } finally {
      setClearing(false);
    }
  }, [campaignId]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5 shrink-0 bg-background">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Chat</span>
          <span className="text-xs text-muted-foreground">— {campaignName}</span>
        </div>
        <TooltipProvider>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleClear}
                  disabled={clearing}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Clear chat history</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onToggleSplit}>
                  <Columns2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {mode === "split" ? "Full chat view" : "Split: chat + data"}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMinimize}>
                  <Minimize2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Minimize to sidebar</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      {/* Chat thread fills remaining space — key forces remount on clear */}
      <ChatThread key={clearKey} campaignId={campaignId} onSideEffect={onSideEffect} />
    </div>
  );
}
